import React, { useRef, useState } from 'react';
import { Flex, Spinner } from '@chakra-ui/react';
import getConfig from 'next/config';

import ManageEvents from './manage-events';
import HelpSection from './help-section';
import ReviewTasks from './review-tasks';
import useUserRoles from 'hooks/useUserRole';
import DashboardStatistics from './dashboard-statistics';
import useUserSubmissionsCount from 'hooks/useUserSubmissions';
import { AuthContextProps } from 'context/AuthProvider';
import IntegrityWorkspaces from './integrity-workspaces';
import OpenCalls from './open-calls';
import AnalyticsDashboard from './analytics-dashboard';

interface Props {
  user?: AuthContextProps;
}

const Dashboard: React.FC<Props> = props => {
  const { publicRuntimeConfig } = getConfig();
  const {
    isOrganizer,
    isPublisherRepresentative,
    isSectionEditor,
    isReviewer,
    isPaperReviewer,
    isLoading,
    hostingEvents,
    reviewingEvents,
    attendingEvents
  } = useUserRoles();

  const [pendingReviewCount, setPendingReviewCount] = useState<number>(0);

  const reviewTasksRef = useRef<{ scrollToReviewTasks: () => void }>(null);

  const handleScrollToReviewTasks = () => {
    if (reviewTasksRef.current) {
      reviewTasksRef.current.scrollToReviewTasks();
    }
  };

  const manageEventsRef = useRef<{ scrollToManageTasks: () => void }>(null);

  const handleScrollToManageTasks = () => {
    if (manageEventsRef.current) {
      manageEventsRef.current.scrollToManageTasks();
    }
  };

  const userId = props.user?.id || '';
  const { submissionCount } = useUserSubmissionsCount(userId);

  if (isLoading) {
    return (
      <Flex justifyContent="center">
        <Spinner />
      </Flex>
    );
  }

  const showManageEvents =
    isOrganizer || isPublisherRepresentative || isSectionEditor;

  return (
    <>
      {props.user?.show_analytics &&
        publicRuntimeConfig.app_env !== 'production' && (
          <AnalyticsDashboard
            publisherId={props.user?.pub_coordinator_in?.[0]}
          />
        )}
      <DashboardStatistics
        pendingReviewCount={pendingReviewCount}
        createdEventsCount={hostingEvents.length}
        submittedResearchCount={submissionCount}
        onScrollToReviewTasks={handleScrollToReviewTasks}
        onScrollToManageTasks={handleScrollToManageTasks}
        onScrollToOpenCalls={handleScrollToReviewTasks}
      />
      <IntegrityWorkspaces />
      {isReviewer || isPaperReviewer ? (
        <ReviewTasks
          ref={reviewTasksRef}
          reviewingEvents={reviewingEvents}
          isLoading={isLoading}
          setPendingReviewCount={setPendingReviewCount}
        />
      ) : null}
      {showManageEvents ? (
        <ManageEvents
          ref={manageEventsRef}
          hostingEvents={hostingEvents}
          reviewingEvents={reviewingEvents}
          attendingEvents={attendingEvents}
          isLoading={isLoading}
        />
      ) : null}
      <OpenCalls />
      <HelpSection />
    </>
  );
};

export default Dashboard;
